/**
 * COMPANY ACCOUNT EMAIL NOTIFICATION SERVICE
 * 
 * Handles sending email notifications for company-related events:
 * - Company registration confirmation
 * - KYC submitted for review
 * - KYC approved
 * - KYC rejected / additional info required
 * - Officer invitation
 */

export interface CompanyEmailData {
 companyName: string;
 registrationNumber: string;
 directorName: string;
 directorEmail: string;
 kycStatus?: string;
 rejectionReason?: string;
 additionalInfoRequired?: string[];
 officerName?: string;
 officerRole?: string;
 invitationLink?: string;
}

// Company branding
const COMPANY_BRANDING = {
 name: 'Aureus Alliance Holdings',
 supportEmail: 'support@aureus.africa',
 website: 'https://aureus.africa',
 dashboardUrl: 'https://aureus.africa/dashboard',
 primaryColor: '#D4AF37',
 logo: ''
};

/**
 * Generate base email template
 */
const generateEmailTemplate = (title: string, content: string): string => {
 return `
<!DOCTYPE html>
<html>
<head>
 <meta charset="utf-8">
 <meta name="viewport" content="width=device-width, initial-scale=1.0">
 <title>${title}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #1F2937;">
 <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
 <div style="background: linear-gradient(135deg, #111827 0%, #1F2937 100%); border-radius: 12px; padding: 30px; border: 1px solid #374151;">
 <!-- Header -->
 <div style="text-align: center; margin-bottom: 30px;">
 <div style="font-size: 48px; margin-bottom: 10px;">${COMPANY_BRANDING.logo}</div>
 <h1 style="color: ${COMPANY_BRANDING.primaryColor}; margin: 0; font-size: 24px;">${COMPANY_BRANDING.name}</h1>
 <p style="color: #9CA3AF; margin: 5px 0 0 0; font-size: 14px;">Corporate Services</p>
 </div>
 
 <!-- Title -->
 <h2 style="color: #F3F4F6; margin: 0 0 20px 0; font-size: 20px;">${title}</h2>
 
 <!-- Content -->
 <div style="color: #D1D5DB;">
 ${content}
 </div>
 
 <!-- CTA Button -->
 <div style="text-align: center; margin: 30px 0;">
 <a href="${COMPANY_BRANDING.dashboardUrl}" style="display: inline-block; background: linear-gradient(135deg, ${COMPANY_BRANDING.primaryColor} 0%, #B8860B 100%); color: #000; text-decoration: none; padding: 12px 30px; border-radius: 8px; font-weight: bold;">
 View Dashboard
 </a>
 </div>
 
 <!-- Footer -->
 <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #374151; text-align: center;">
 <p style="color: #6B7280; font-size: 12px; margin: 0;">
 This email was sent by ${COMPANY_BRANDING.name}<br>
 For questions, contact <a href="mailto:${COMPANY_BRANDING.supportEmail}" style="color: ${COMPANY_BRANDING.primaryColor};">${COMPANY_BRANDING.supportEmail}</a>
 </p>
 </div>
 </div>
 </div>
</body>
</html>`;
};

/**
 * Send company registration confirmation email
 */
export async function sendRegistrationConfirmationEmail(data: CompanyEmailData): Promise<{ success: boolean; error?: string }> {
 try {
 const { companyName, registrationNumber, directorName, directorEmail } = data;
 
 const title = ' Company Registration Submitted';
 const content = `
 <p>Hello <strong>${directorName}</strong>,</p>
 <p>Thank you for registering your company with ${COMPANY_BRANDING.name}. Your company registration has been submitted successfully.</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Company Name:</strong> <span style="color: ${COMPANY_BRANDING.primaryColor};">${companyName}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Registration Number:</strong> <span style="color: #F3F4F6;">${registrationNumber}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: #F59E0B;">Draft - Pending Documents</span></p>
 </div>
 <h3 style="color: #F3F4F6; margin-top: 20px;">Next Steps:</h3>
 <ol style="color: #D1D5DB; padding-left: 20px;">
 <li style="margin: 10px 0;">Upload required company documents (Certificate of Incorporation, MOI, etc.)</li>
 <li style="margin: 10px 0;">Add any additional officers or shareholders</li>
 <li style="margin: 10px 0;">Submit your company for KYC review</li>
 </ol>
 <p>Once approved, your company will be able to participate in investment opportunities.</p>
 `;

 return await sendCompanyEmail(directorEmail, `${title} - ${companyName}`, title, content, 'company_registration');
 } catch (err) {
 console.error('Error sending registration confirmation email:', err);
 return { success: false, error: 'Failed to send email' };
 }
}

/**
 * Send KYC submitted for review email
 */
export async function sendKYCSubmittedEmail(data: CompanyEmailData): Promise<{ success: boolean; error?: string }> {
 try {
 const { companyName, directorName, directorEmail } = data;
 
 const title = ' KYC Review Submitted';
 const content = `
 <p>Hello <strong>${directorName}</strong>,</p>
 <p>Your company <strong style="color: ${COMPANY_BRANDING.primaryColor};">${companyName}</strong> has been submitted for KYC review.</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: #3B82F6;"> Under Review</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Expected Time:</strong> <span style="color: #F3F4F6;">2-5 business days</span></p>
 </div>
 <p>Our compliance team will review your submission and documents. You will receive an email notification once the review is complete.</p>
 <p style="color: #F59E0B; margin-top: 20px;"> Please note: You cannot edit your company registration details while the review is in progress.</p>
 `;

 return await sendCompanyEmail(directorEmail, `${title} - ${companyName}`, title, content, 'company_kyc_submitted');
 } catch (err) {
 console.error('Error sending KYC submitted email:', err);
 return { success: false, error: 'Failed to send email' };
 }
}

/**
 * Send KYC approved email
 */
export async function sendKYCApprovedEmail(data: CompanyEmailData): Promise<{ success: boolean; error?: string }> {
 try {
 const { companyName, registrationNumber, directorName, directorEmail } = data;

 const title = ' Company KYC Approved';
 const content = `
 <p>Hello <strong>${directorName}</strong>,</p>
 <p>Great news! Your company <strong style="color: ${COMPANY_BRANDING.primaryColor};">${companyName}</strong> has been approved and verified.</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Company Name:</strong> <span style="color: ${COMPANY_BRANDING.primaryColor};">${companyName}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Registration Number:</strong> <span style="color: #F3F4F6;">${registrationNumber}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Status:</strong> <span style="color: #22C55E;"> Approved & Verified</span></p>
 </div>
 <p>Your company can now:</p>
 <ul style="color: #D1D5DB; padding-left: 20px;">
 <li style="margin: 8px 0;">Participate in corporate investment opportunities</li>
 <li style="margin: 8px 0;">Purchase shares on behalf of the company</li>
 <li style="margin: 8px 0;">Receive corporate dividends and distributions</li>
 </ul>
 <p style="color: #22C55E; margin-top: 20px;">Thank you for completing your company verification!</p>
 `;

 return await sendCompanyEmail(directorEmail, `${title} - ${companyName}`, title, content, 'company_kyc_approved');
 } catch (err) {
 console.error('Error sending KYC approved email:', err);
 return { success: false, error: 'Failed to send email' };
 }
}

/**
 * Send KYC rejected email
 */
export async function sendKYCRejectedEmail(data: CompanyEmailData): Promise<{ success: boolean; error?: string }> {
 try {
 const { companyName, directorName, directorEmail, rejectionReason, additionalInfoRequired } = data;

 const title = ' Company KYC Requires Attention';
 const content = `
 <p>Hello <strong>${directorName}</strong>,</p>
 <p>Unfortunately, we were unable to approve the KYC submission for <strong style="color: ${COMPANY_BRANDING.primaryColor};">${companyName}</strong>.</p>
 <div style="background-color: #7F1D1D; padding: 20px; border-radius: 8px; margin: 20px 0; border: 1px solid #DC2626;">
 <p style="margin: 0 0 10px 0; color: #FCA5A5; font-weight: bold;">Reason:</p>
 <p style="margin: 0; color: #F3F4F6;">${rejectionReason || 'Additional documentation or information is required.'}</p>
 </div>
 ${additionalInfoRequired && additionalInfoRequired.length > 0 ? `
 <p style="color: #F3F4F6; margin-top: 20px;">Please provide the following:</p>
 <ul style="color: #D1D5DB; padding-left: 20px;">
 ${additionalInfoRequired.map(item => `<li style="margin: 8px 0;">${item}</li>`).join('')}
 </ul>
 ` : ''}
 <p style="margin-top: 20px;">Please log in to your dashboard to update your company information and resubmit for review.</p>
 <p style="color: #9CA3AF; font-size: 14px;">If you have any questions, please contact our support team at <a href="mailto:${COMPANY_BRANDING.supportEmail}" style="color: ${COMPANY_BRANDING.primaryColor};">${COMPANY_BRANDING.supportEmail}</a></p>
 `;

 return await sendCompanyEmail(directorEmail, `${title} - ${companyName}`, title, content, 'company_kyc_rejected');
 } catch (err) {
 console.error('Error sending KYC rejected email:', err);
 return { success: false, error: 'Failed to send email' };
 }
}

/**
 * Send officer invitation email
 */
export async function sendOfficerInvitationEmail(data: CompanyEmailData): Promise<{ success: boolean; error?: string }> {
 try {
 const { companyName, directorEmail, officerName, officerRole, invitationLink } = data;

 const title = ' Company Officer Invitation';
 const content = `
 <p>Hello <strong>${officerName || 'there'}</strong>,</p>
 <p>You have been added as a <strong style="color: ${COMPANY_BRANDING.primaryColor};">${officerRole || 'officer'}</strong> for the company <strong>${companyName}</strong> on ${COMPANY_BRANDING.name}.</p>
 <div style="background-color: #111827; padding: 20px; border-radius: 8px; margin: 20px 0;">
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Company:</strong> <span style="color: ${COMPANY_BRANDING.primaryColor};">${companyName}</span></p>
 <p style="margin: 5px 0;"><strong style="color: #9CA3AF;">Your Role:</strong> <span style="color: #F3F4F6;">${officerRole || 'Officer'}</span></p>
 </div>
 <p>To complete your registration and verify your identity, please click the button below:</p>
 ${invitationLink ? `
 <div style="text-align: center; margin: 30px 0;">
 <a href="${invitationLink}" style="display: inline-block; background: linear-gradient(135deg, ${COMPANY_BRANDING.primaryColor} 0%, #B8860B 100%); color: #000; text-decoration: none; padding: 12px 30px; border-radius: 8px; font-weight: bold;">
 Accept Invitation
 </a>
 </div>
 ` : ''}
 <p style="color: #9CA3AF; font-size: 14px;">If you did not expect this invitation, please disregard this email or contact <a href="mailto:${COMPANY_BRANDING.supportEmail}" style="color: ${COMPANY_BRANDING.primaryColor};">${COMPANY_BRANDING.supportEmail}</a></p>
 `;

 return await sendCompanyEmail(directorEmail, `${title} - ${companyName}`, title, content, 'company_officer_invitation');
 } catch (err) {
 console.error('Error sending officer invitation email:', err);
 return { success: false, error: 'Failed to send email' };
 }
}

/**
 * Internal helper to send company emails via API
 */
async function sendCompanyEmail(
 toEmail: string,
 subject: string,
 title: string,
 content: string,
 category: string
): Promise<{ success: boolean; error?: string }> {
 try {
 const htmlContent = generateEmailTemplate(title, content);

 const response = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 emailType: 'custom',
 data: {
 email: toEmail,
 subject,
 htmlContent,
 textContent: content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim(),
 category
 }
 })
 });

 const result = await response.json();

 if (!response.ok || !result.success) {
 console.error(`Failed to send company email (${category}):`, result.error);
 return { success: false, error: result.error || 'Failed to send email' };
 }

 console.log(`Company email sent (${category}) to ${toEmail}`);
 return { success: true };
 } catch (err) {
 console.error(`Error sending company email (${category}):`, err);
 return { success: false, error: 'Failed to send email' };
 }
}

